import { Link, Switch } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IS3AccessPoint } from '~/src/dash/api/s3-access-points';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  Col,
  Cols,
  ISimpleOption,
  ISimpleOptionSelect,
  Row,
  Rows,
  Text,
} from '~/src/dash/publicaUI';
import { extractRouteParams, getRoutePath, RoutePublisherSettings } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  deliveries,
  EReportDelivery,
  EReportFrequency,
  EReportPeriod,
  frequencies,
  monthlyScheduledDays,
  periods,
  weeklyScheduledDays,
} from '~/src/dash/views/reporting/common';
import { Recipients } from '~/src/dash/views/reporting/ReportBuilder/Schedule/Recipients';

interface IErrors {
  delivery: string | JSX.Element;
  frequency: string | JSX.Element;
  period: string | JSX.Element;
  scheduledDay: string | JSX.Element;
}

export interface IScheduleConfig {
  delivery: string;
  frequency: string;
  isSchedulingActive: boolean;
  period: string;
  recipients: Array<string>;
}

interface IScheduleSectionProps {
  isSchedulingActive: boolean;
  scheduledDay: string;
  delivery: string;
  frequency: string;
  period: string;
  recipients: Array<string>;
  s3AccessPoint: IS3AccessPoint | null;
  setScheduledDay: (scheduledDay: string) => void;
  setIsSchedulingActive: (active: boolean) => void;
  setPeriod: (period: string) => void;
  setFrequency: (frequency: string) => void;
  setDelivery: (delivery: string) => void;
  setRecipients: (recipients: string[]) => void;
}

export const ScheduleSection = ({
  isSchedulingActive,
  scheduledDay,
  delivery,
  frequency,
  period,
  recipients,
  s3AccessPoint,
  setScheduledDay,
  setIsSchedulingActive,
  setPeriod,
  setFrequency,
  setDelivery,
  setRecipients,
}: IScheduleSectionProps) => {
  const defaultError: IErrors = {
    delivery: '',
    frequency: '',
    period: '',
    scheduledDay: '',
  };

  const { session, user } = useBaseStore();

  const [errors, setErrors] = useState<IErrors>(defaultError);

  // validate the schedule setting
  const isScheduleSettingValid = () => {
    const newErrors: IErrors = cloneDeep(defaultError);
    // Cannot schedule year-to-date report on a daily interval
    if (
      Number(period) === EReportPeriod.CurrentYear &&
      Number(frequency) === EReportFrequency.FrequencyDaily
    ) {
      newErrors.period = 'Cannot schedule year-to-date report on a daily interval.';
    }

    // cannot schedule for AWS S3 if publisher is missing S3 access point credentials
    if (Number(delivery) === EReportDelivery.AWSS3 && isEmpty(s3AccessPoint)) {
      newErrors.delivery = (
        <Text small color={publicaColors.orange}>
          Input access point credentials in{' '}
          <Link
            target='_blank'
            style={{
              textDecoration: 'underline',
              color: publicaColors.orange,
              display: 'inline-block',
            }}
            href={
              '#' +
              getRoutePath(
                RoutePublisherSettings,
                extractRouteParams({
                  session,
                  user,
                }),
              )
            }
          >
            General Settings
          </Link>{' '}
          to use S3 delivery option.
        </Text>
      );
    }

    return isSchedulingActive ? newErrors : defaultError;
  };

  // helper to format options for SimpleSingleSelect
  const getOptions = (
    items: Array<ISimpleOption>,
    selectedValue: string,
    isOptionValid: (value: ISimpleOption) => boolean = () => false,
  ): Array<ISimpleOptionSelect> => {
    return items.map((item) => ({
      ...item,
      isSelected: item.value === selectedValue,
      isDisabled: !isOptionValid(item),
    }));
  };

  const onFrequencyChange = (value: string) => {
    unstable_batchedUpdates(() => {
      setFrequency(value);
      setScheduledDay('1');
    });
  };

  // isFrequencyOptionValid & isPeriodOptionValid
  // help to invalid scheduling year-to-date report on a daily interval
  const isFrequencyOptionValid = (frequencyOption: ISimpleOption): boolean => {
    if (
      Number(period) === EReportPeriod.CurrentYear &&
      Number(frequencyOption.value) === EReportFrequency.FrequencyDaily
    ) {
      return false;
    }
    return true;
  };

  const isPeriodOptionValid = (periodOption: ISimpleOption): boolean => {
    if (
      Number(periodOption.value) === EReportPeriod.CurrentYear &&
      Number(frequency) === EReportFrequency.FrequencyDaily
    ) {
      return false;
    }
    return true;
  };

  const getScheduledDayOptions = (frequency: string): ISimpleOption[] => {
    return frequency === String(EReportFrequency.FrequencyMonthly)
      ? monthlyScheduledDays
      : weeklyScheduledDays;
  };

  // handle change whenever the schedule setting updates
  useEffect(() => {
    const errors = isScheduleSettingValid();
    setErrors(errors);
  }, [isSchedulingActive, period, frequency, delivery, recipients, scheduledDay]);

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium} style={{ alignItems: 'center' }}>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <IconV2 name='schedule' large />
          </Col>
          <Col>
            <Text sizeV2 bold large>
              Schedule
            </Text>
          </Col>
        </Cols>
      </Row>

      <Rows extraLargeHorizontalPaddingV2>
        <Row noVerticalPadding marginBottom={spacingV2.small}>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '7rem' }}>
              <Text bold small>
                Status
              </Text>
            </Col>
            <Col style={{ marginRight: spacingV2.medium }}>
              <Switch
                checked={isSchedulingActive}
                onChange={() => setIsSchedulingActive(!isSchedulingActive)}
                name='Status'
              />
            </Col>
            <Col>
              <Text small bold muted={!isSchedulingActive}>
                {isSchedulingActive ? 'Active' : 'Inactive'}
              </Text>
            </Col>
          </Cols>
        </Row>

        <Row noVerticalPadding>
          <Cols wrap style={{ alignItems: 'center' }}>
            <Col marginRight={spacingV2.huge} marginBottom={spacingV2.medium}>
              <Cols style={{ alignItems: 'center' }}>
                <Col style={{ minWidth: '7rem' }}>
                  <Text bold small muted={!isSchedulingActive}>
                    Frequency
                  </Text>
                </Col>
                <Col>
                  <SimpleSingleSelect
                    isDisabled={!isSchedulingActive}
                    inputWidth='12.5rem'
                    onChange={(id) => onFrequencyChange(id)}
                    options={getOptions(frequencies, frequency, isFrequencyOptionValid)}
                    popoverWidth='12.5rem'
                  />
                </Col>
              </Cols>
            </Col>

            {/* TODO: put back in if we add Scheduled Dates functionality */}
            {/* <Col marginBottom={spacingV2.medium}>
              <Cols style={{ alignItems: 'center' }}>
                <Col>
                  <Cols style={{ alignItems: 'center' }}>
                    <Col style={{ minWidth: '7rem' }}>
                      <Text small bold muted={!isSchedulingActive}>
                        Start - End Date
                      </Text>
                    </Col>
                    <Col>
                      <CalendarV2
                        isDisabled={!isSchedulingActive}
                        minDate={today}
                        calStartDate={dateRange ? dateRange[0] : today}
                        calEndDate={dateRange ? dateRange[1] : today}
                        handleApplyDatesChange={({ newStartDate, newEndDate }) =>
                          setScheduledDateRange([newStartDate, newEndDate])
                        }
                        uniqueID='reportScheduleCal'
                        openDirection='up'
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Col> */}
          </Cols>
          {errors.frequency && (
            <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
              {errors.frequency}
            </Text>
          )}
        </Row>

        {[
          String(EReportFrequency.FrequencyMonthly),
          String(EReportFrequency.FrequencyWeekly),
        ].includes(frequency) && (
          <Row noVerticalPadding marginBottom={spacingV2.medium}>
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ minWidth: '7rem' }}>
                <Text bold small muted={!isSchedulingActive}>
                  Scheduled Day
                </Text>
              </Col>
              <Col displayFlex>
                <SimpleSingleSelect
                  isDisabled={!isSchedulingActive}
                  inputWidth='12.5rem'
                  popoverWidth='12.5rem'
                  options={getOptions(getScheduledDayOptions(frequency), scheduledDay, () => true)}
                  onChange={(value) => setScheduledDay(value)}
                />
              </Col>
            </Cols>
            {errors.scheduledDay && (
              <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
                {errors.scheduledDay}
              </Text>
            )}
          </Row>
        )}

        <Row noVerticalPadding marginBottom={spacingV2.medium}>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '7rem' }}>
              <Text bold small muted={!isSchedulingActive}>
                Period Range
              </Text>
            </Col>
            <Col>
              <Cols wrap style={{ alignItems: 'center' }}>
                <Col marginRight={spacingV2.medium}>
                  <SimpleSingleSelect
                    isDisabled={!isSchedulingActive}
                    inputWidth='12.5rem'
                    options={getOptions(periods, period, isPeriodOptionValid)}
                    onChange={(id) => setPeriod(id)}
                    popoverWidth='12.5rem'
                  />
                </Col>
                <Col>
                  <Text extraSmall muted>
                    As scheduling makes this report a recurring one, period range operates as a
                    moving preset date range.
                  </Text>
                </Col>
              </Cols>
            </Col>
          </Cols>
          {errors.period && (
            <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
              {errors.period}
            </Text>
          )}
        </Row>

        <Row noVerticalPadding marginBottom={spacingV2.medium}>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '7rem' }}>
              <Text bold small muted={!isSchedulingActive}>
                Delivery
              </Text>
            </Col>
            <Col displayFlex>
              <PillSwitcherV2
                size='small'
                isDisabled={!isSchedulingActive}
                options={getOptions(deliveries, delivery)}
                onChange={(value) => setDelivery(value)}
              />
            </Col>
          </Cols>
          {errors.delivery && (
            <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
              {errors.delivery}
            </Text>
          )}
        </Row>

        <Row noVerticalPadding>
          <Recipients
            recipients={recipients}
            onChange={setRecipients}
            isDisabled={!isSchedulingActive || Number(delivery) === EReportDelivery.AWSS3}
          />
        </Row>
      </Rows>
    </Rows>
  );
};
